import type { AxiosInstance, AxiosRequestConfig, Method } from 'axios';
import axios from 'axios';
import type { BaseModel } from '~/api/models/BaseModel';
import { Logger, Router } from '~/core';
import { RequestError, TranslatableError } from '~/errors';
import { camelCaseToUnderscore } from '~/helpers';
import { useUserStore } from '~/stores';
import { useModelsStore } from '~/stores/models';

type callbackFn = () => any;
type HTTPMethods = 'get' | 'post' | 'put' | 'delete' | 'head';

class CSRFClass {
    private ran: Promise<any> | undefined;

    initialize(axios: AxiosInstance) {
        if (this.ran) return this.ran;

        Logger.debug('CSRFClass', 'Initializing CSRF...');
        return (this.ran = axios.request({
            method: 'GET',
            baseURL: window.Wisp.BaseURL,
            url: '/sanctum/csrf-cookie',
        }));
    }
}
const CSRF = new CSRFClass();

// Global context for route parameters during navigation
let pendingRouteParams: Record<string, any> = {};

// Global functions to manage route parameters
export function setPendingRouteParams(params: Record<string, any>) {
    pendingRouteParams = params;
}

export function clearPendingRouteParams() {
    pendingRouteParams = {};
}

export default class RequestService {
    private axios: AxiosInstance;
    private responseCache: Record<string, Promise<any>> = {};
    private ready = false;
    private queue: callbackFn[] = [];

    constructor(private namespace: string) {
        // /auth/@me should be ignored
        this.axios = axios.create({
            baseURL: window.Wisp ? window.Wisp.BaseURL + `/api/${namespace}` : undefined,
            timeout: 60000,
            responseType: 'json',
            headers: {
                // TODO: Me
            },
            withCredentials: true,
        });

        if (window.Wisp) {
            CSRF.initialize(this.axios)
                .then(() => {
                    Logger.debug(`RequestService[${namespace}]`, 'Ready to perform requests!');
                    this.ready = true;

                    this.queue.forEach((a) => a());
                    this.queue = [];
                })
                .catch((err) => {
                    Logger.fatal('RequestService', 'Initializing CSRF failed', err);
                });
        }
    }

    baseURL() {
        return this.axios.defaults.baseURL;
    }

    // TODO: What if there's too many requests queued? e.g. due to page loads
    // Timeouts or just discard...?
    private waitForReady(callbackFn: () => any) {
        return new Promise((resolve, reject) => {
            this.queue.push(() => {
                callbackFn().then(resolve).catch(reject);
            });
        });
    }

    private async injectRouterParameters(data: string): Promise<string> {
        const modelsStore = useModelsStore();

        // Use current route params directly from Vue Router instance
        let params = Router.currentRoute.value.params || {};

        // If route params are empty, try pending route params from navigation context
        if (Object.keys(params).length === 0 && Object.keys(pendingRouteParams).length > 0) {
            params = pendingRouteParams;
        }

        // Fallback: merge both current and pending params to ensure we have all parameters
        if (Object.keys(pendingRouteParams).length > 0) {
            params = { ...params, ...pendingRouteParams };
        }

        // Map route parameter names to model store getters
        const modelGetterMap: Record<string, (store: any) => any> = {
            server: (store) => store.server,
            user: (store) => store.user,
            databaseHost: (store) => store.databaseHost,
            location: (store) => store.location,
            node: (store) => store.node,
            mod: (store) => store.mod,
            nest: (store) => store.nest,
            egg: (store) => store.egg,
            apiKey: (store) => store.apiKey,
            schedule: (store) => store.schedule,
            domain: (store) => store.domain,
        };

        for (const key in params) {
            const modelGetter = modelGetterMap[key];
            const model = modelGetter ? modelGetter(modelsStore) : undefined;
            const value = model?.getRouteID?.(this.namespace) || (params[key] as string);

            if (value) {
                data = data.replaceAll(`:${key}`, value);
            }
        }
        return data;
    }
    private async request(method: Method, endpoint: string, ...args: any[]): Promise<any> {
        if (!this.ready)
            return this.waitForReady(() => {
                return this.request(method, endpoint, ...args);
            });

        endpoint = await this.injectRouterParameters(endpoint);
        return this.axios[method.toLowerCase() as HTTPMethods](endpoint, ...args)
            .then(async (res) => {
                if (import.meta.env.DEV && import.meta.env.VITE_HTTP_DELAY) {
                    await new Promise((resolve) => setTimeout(resolve, import.meta.env.VITE_HTTP_DELAY));
                }

                // If res.data is empty, it can be caused by e.g. malformed JSON response
                if (res.status >= 200 && res.status < 300 && res.status !== 204 && !res.data) {
                    throw new Error("Received malformed JSON or status code isn't 204...?");
                }

                if (res.data?.errors && res.data?.errors.length > 0) {
                    const errors = res.data.errors;

                    if (errors.length > 1) {
                        // If we receive multiple errors, chances are this is a validation response
                        // TODO: these errors should be translated
                        throw new TranslatableError(['components.form.validation_error'], errors);
                    } else {
                        // Otherwise, its a DisplayApiException
                        const { code, data } = errors.shift();
                        throw new TranslatableError([code, data]);
                    }
                }

                return res.data;
            })
            .catch(async (err) => {
                if (err instanceof TranslatableError) {
                    throw err;
                }
                const refresh = () => {
                    const lastRefresh = parseInt(localStorage.getItem('last_refresh') || '');
                    if (!isNaN(lastRefresh)) {
                        const date = new Date(lastRefresh);

                        // If the last refresh was inside a minute, do not reload due to possible infinite refresh loop
                        if (new Date().getTime() - date.getTime() < 60 * 1000) return;
                    }

                    Logger.debug('RequestService', 'Refreshing the page...');
                    localStorage.setItem('last_refresh', new Date().getTime().toString());
                    location.reload();
                };

                switch (err.response?.status) {
                    case 301:
                    case 302:
                        // Assume if the backend redirects us it means the user is already authenticated.
                        refresh();
                        break;
                    case 400:
                    case 422: {
                        console.log(err.response?.data);

                        const responseErrors = err.response?.data?.errors;

                        // DisplayApiException returns an array of {code, data} objects
                        if (Array.isArray(responseErrors) && responseErrors.length > 0) {
                            if (responseErrors.length > 1) {
                                throw new TranslatableError(['components.form.validation_error'], responseErrors);
                            }

                            const { code, data } = responseErrors[0];
                            throw new TranslatableError([code, data]);
                        }

                        // Standard Laravel validation errors (object keyed by field names)
                        const errors: any[] = Object.values(responseErrors || {});

                        const errorMessages = errors
                            .map((e) => {
                                if (Array.isArray(e)) return e[0];
                                return e.detail;
                            })
                            .filter(Boolean);

                        if (errors.length) {
                            throw new TranslatableError(
                                ['components.form.validation_error', errors.length],
                                errorMessages.map((error) => {
                                    return ['_raw', error];
                                }),
                            );
                        }
                        break;
                    }
                    case 401:
                        const userStore = useUserStore();
                        userStore.set(undefined);

                        refresh();
                        break;
                    case 403:
                        // TODO: display that no perms (although this should never realistically happen)
                        break;
                    case 404:
                        if (err.response?.data?.errors?.[0]?.data?.code === 404) {
                            void import('./daemon/wrapper').then(({ default: DaemonWrapper }) => DaemonWrapper.disconnect());
                        }

                        /*Router.replace({
                            name: '404',
                            params: {
                                catchAll: location.pathname.substring(1).split('/'),
                            },
                            query: Router.currentRoute.value.query,
                            hash: Router.currentRoute.value.hash,
                        });*/
                        break;
                    case 419:
                        refresh();
                        break;
                    case 429:
                        throw new TranslatableError(['navigation.errors.429']);
                }

                throw new RequestError(method, endpoint, err);
            });
    }

    get(endpoint: string, data?: Record<string, any>, config?: AxiosRequestConfig) {
        return this.request('GET', `${endpoint}${data ? `?${this.toQuery(data)}` : ''}`, config);
    }

    async getCached(cacheKey: string, endpoint: string, data?: Record<string, any>, config?: AxiosRequestConfig): Promise<any> {
        cacheKey = await this.injectRouterParameters(cacheKey);
        if (data) {
            cacheKey += `?${JSON.stringify(data)}`; // Although not correct way, it's the lazy:tm: way
        }

        if (cacheKey in this.responseCache) {
            Logger.debug('RequestService', `Trying to refetch ${cacheKey}, using local cache instead...`);

            return this.responseCache[cacheKey];
        }

        return (this.responseCache[cacheKey] = new Promise((resolve, reject) => {
            this.get(endpoint, data, config)
                .then(resolve)
                .catch((err) => {
                    delete this.responseCache[cacheKey];

                    reject(err);
                });
        }));
    }

    post(endpoint: string, data?: Record<string, any>, config?: AxiosRequestConfig) {
        return this.request('POST', endpoint, data, config);
    }

    patch(endpoint: string, data?: Record<string, any>, config?: AxiosRequestConfig) {
        return this.request('PATCH', endpoint, data, config);
    }

    put(endpoint: string, data?: Record<string, any>, config?: AxiosRequestConfig) {
        return this.request('PUT', endpoint, data, config);
    }

    delete(endpoint: string, config?: AxiosRequestConfig) {
        return this.request('DELETE', endpoint, config);
    }

    toQuery(data: Record<string, unknown>) {
        const res: string[] = [];

        const normalizeKey = (key: string, parent?: string) => {
            if (parent) {
                return `${parent}[${camelCaseToUnderscore(key)}]`;
            }

            return key.includes('[') ? key : camelCaseToUnderscore(key);
        };

        const append = (key: string, value: unknown) => {
            if (value === undefined || value === null || value === '') {
                return;
            }

            if (Array.isArray(value)) {
                value.forEach((item) => {
                    append(`${key}[]`, item);
                });

                return;
            }

            if (typeof value === 'object') {
                Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) => {
                    append(normalizeKey(nestedKey, key), nestedValue);
                });

                return;
            }

            res.push(`${key}=${encodeURIComponent(String(value))}`);
        };

        Object.entries(data).forEach(([key, value]) => {
            append(normalizeKey(key), value);
        });

        return res.join('&');
    }

    // Technically doesn't belong here, but it should be available as an easy helper method
    // TODO: consider handling this automatically in patch/put instead?
    async updateModelBinding<T extends BaseModel>(model: T): Promise<T> {
        const modelsStore = useModelsStore();
        const attributes = model.getAttributes();
        const relationships = model.relationships || {};

        if (Object.keys(relationships).length > 0) {
            attributes.update_relationships = relationships;
        }

        modelsStore.updateModel({
            name: model.getRouteName() as any,
            model: attributes,
        });

        return model;
    }
}
